import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Lead, leadsApi, LeadStage } from '../services/api';
import toast from 'react-hot-toast';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: Lead; // Dados para edição (opcional)
}

const STAGE_OPTIONS: { id: LeadStage; label: string }[] = [
  { id: 'new', label: 'Prospecção' },
  { id: 'prospecting', label: 'Prospecção Avançada' },
  { id: 'contacted', label: 'Contactados' },
  { id: 'negotiation', label: 'Em Negociação' },
  { id: 'proposal', label: 'Proposta Enviada' },
  { id: 'pending', label: 'Aguardando' },
  { id: 'won', label: 'Fechado Ganho' },
  { id: 'lost', label: 'Fechado Perdido' },
];

const SOURCE_OPTIONS: { id: NonNullable<Lead['source']>; label: string }[] = [
  { id: 'website', label: 'Website' },
  { id: 'referral', label: 'Indicação / Referência' },
  { id: 'social', label: 'Redes Sociais' },
  { id: 'cold_call', label: 'Chamada a frio' },
  { id: 'walk_in', label: 'Passou pelo balcão' },
  { id: 'other', label: 'Outro' },
];

export const LeadFormModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, initialData }) => {
  const [formData, setFormData] = useState<Partial<Lead>>({
    name: '',
    email: '',
    phone: '',
    stage: 'new',
    source: 'other',
    dealValue: 0,
    expectedCloseDate: '',
    lostReason: '',
    notes: '',
    isPublic: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEdit = !!initialData;

  // Atualiza formData quando initialData mudar (importante para edição)
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        stage: initialData.stage || 'new',
        source: initialData.source || 'other',
        dealValue: initialData.dealValue || 0,
        expectedCloseDate: initialData.expectedCloseDate
          ? initialData.expectedCloseDate.toString().slice(0, 10)
          : '',
        lostReason: initialData.lostReason || '',
        notes: initialData.notes || '',
        isPublic: initialData.isPublic || false,
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        stage: 'new',
        source: 'other',
        dealValue: 0,
        expectedCloseDate: '',
        lostReason: '',
        notes: '',
        isPublic: false,
      });
    }
  }, [initialData, isOpen]);

  // Foco automático no primeiro input ao abrir
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        document.querySelector('input[name="name"]')?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!formData.name?.trim() || !formData.email?.trim()) {
    toast.error('Nome e Email são obrigatórios');
    return;
  }

  setIsSubmitting(true);

  try {
    if (isEdit && initialData?._id) {
      await leadsApi.update(initialData._id, formData);
      toast.success('Lead atualizado com sucesso!');
    } else {
      await leadsApi.create({
        ...formData,
        stage: (formData.stage as LeadStage) || 'new',
      });
      toast.success('Lead criado com sucesso!');
      
      // RESETAR O FORMULÁRIO AQUI (apenas para novas criações)
      setFormData({
        name: '',
        email: '',
        phone: '',
        stage: 'new',
        source: 'other',
        dealValue: 0,
        expectedCloseDate: '',
        lostReason: '',
        notes: '',
        isPublic: false,
      });
    }

    onSuccess();
    onClose();
  } catch (err: any) {
    console.error('Erro ao salvar lead:', err);
    toast.error(err.message || 'Erro ao guardar lead. Tente novamente.');
  } finally {
    setIsSubmitting(false);
  }
};

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3 sm:p-4 md:p-6 overflow-y-auto">
  <div className="bg-white rounded-2xl w-full max-w-xl md:max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] my-auto">
    
    {/* Header - Fixo no topo do modal */}
    <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 sm:px-6 py-4 text-white flex items-center justify-between shrink-0">
      <h2 className="text-lg sm:text-xl font-bold">
        {isEdit ? 'Editar Lead' : 'Novo Lead'}
      </h2>
      <button
        onClick={onClose}
        className="p-1.5 hover:bg-white/20 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
        aria-label="Fechar modal"
      >
        <X size={20} className="sm:w-6 sm:h-6" />
      </button>
    </div>

    {/* Corpo com Scroll Interno para evitar cortes em telas pequenas/orientação horizontal */}
    <form onSubmit={handleSubmit} className="p-4 sm:p-6 flex flex-col gap-4 sm:gap-5 overflow-y-auto flex-1">
      
      {/* Campo: Nome */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Nome <span className="text-red-500">*</span>
        </label>
        <input
          name="name"
          type="text"
          value={formData.name || ''}
          onChange={e => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3.5 py-2.5 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
          required
          placeholder="Nome completo do lead"
        />
      </div>

      {/* Grid de Contato: Email + Telefone */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            name="email"
            type="email"
            value={formData.email || ''}
            onChange={e => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-3.5 py-2.5 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
            required
            placeholder="exemplo@dominio.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Telefone
          </label>
          <input
            name="phone"
            type="tel"
            value={formData.phone || ''}
            onChange={e => setFormData({ ...formData, phone: e.target.value })}
            className="w-full px-3.5 py-2.5 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
            placeholder="+258 84 XXX XXXX"
          />
        </div>
      </div>

      {/* Grid de Negócio: Estágio + Origem + Valor + Data */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Estágio
          </label>
          <select
            name="stage"
            value={formData.stage || 'new'}
            onChange={e => setFormData({ ...formData, stage: e.target.value as LeadStage })}
            className="w-full px-3.5 py-2.5 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white transition"
          >
            {STAGE_OPTIONS.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Origem
          </label>
          <select
            name="source"
            value={formData.source || 'other'}
            onChange={e => setFormData({ ...formData, source: e.target.value as Lead['source'] })}
            className="w-full px-3.5 py-2.5 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white transition"
          >
            {SOURCE_OPTIONS.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Valor do Negócio (MT)
          </label>
          <input
            name="dealValue"
            type="number"
            min="0"
            step="0.01"
            value={formData.dealValue ?? 0}
            onChange={e => setFormData({ ...formData, dealValue: Number(e.target.value) || 0 })}
            className="w-full px-3.5 py-2.5 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
            placeholder="0,00"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Data Prevista de Fecho
          </label>
          <input
            name="expectedCloseDate"
            type="date"
            value={formData.expectedCloseDate || ''}
            onChange={e => setFormData({ ...formData, expectedCloseDate: e.target.value })}
            className="w-full px-3.5 py-2.5 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
          />
        </div>
      </div>

      {formData.stage === 'lost' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Motivo da Perda
          </label>
          <textarea
            name="lostReason"
            value={formData.lostReason || ''}
            onChange={e => setFormData({ ...formData, lostReason: e.target.value })}
            rows={2}
            className="w-full px-3.5 py-2.5 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none transition"
            placeholder="Porque é que este negócio não avançou? (preço, timing, concorrência...)"
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Notas / Observações
        </label>
        <textarea
          name="notes"
          value={formData.notes || ''}
          onChange={e => setFormData({ ...formData, notes: e.target.value })}
          rows={3}
          className="w-full px-3.5 py-2.5 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none transition"
          placeholder="Informações adicionais sobre o lead..."
        />
      </div>

      {/* Checkbox de visibilidade */}
      <div className="flex items-start gap-3 py-1">
        <input
          type="checkbox"
          id="isPublic"
          checked={formData.isPublic}
          onChange={e => setFormData({ ...formData, isPublic: e.target.checked })}
          className="w-4 h-4 mt-0.5 text-blue-600 rounded focus:ring-blue-500 border-gray-300 shrink-0 cursor-pointer"
        />
        <label htmlFor="isPublic" className="text-sm text-gray-700 cursor-pointer select-none leading-tight">
          Tornar este lead visível para toda a equipa
        </label>
      </div>

      {/* Botões de Ação - Acompanham o scroll e mantêm espaçamento */}
      <div className="flex flex-col-reverse sm:flex-row gap-3 pt-3 mt-auto border-t border-gray-100">
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="w-full sm:w-1/2 py-2.5 sm:py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition active:scale-[0.98]"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full sm:w-1/2 py-2.5 sm:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-70 shadow-sm"
        >
          {isSubmitting && <Loader2 size={18} className="animate-spin" />}
          {isSubmitting ? 'A guardar...' : isEdit ? 'Atualizar Lead' : 'Criar Lead'}
        </button>
      </div>
    </form>
  </div>
</div>
  );
};